// Canonical app-level phases (colors, labels, data aggregation)
export const PHASE_KEYS = [
  "stable",
  "metastable",
  "transition",
  "solitons",
  "unknown",
] as const;

export type PhaseKey = typeof PHASE_KEYS[number];

export const PHASE_LABELS: Record<PhaseKey, string> = {
  stable: "Stable",
  metastable: "Metastable",
  transition: "Transition",
  solitons: "Solitons",
  unknown: "Unknown",
} as const;

/** CSS variable names for each PhaseKey */
export const PHASE_VAR_NAMES: Record<PhaseKey, `--${string}`> = {
  stable: "--stable",
  metastable: "--metastable",
  transition: "--transition",
  solitons: "--solitons",
  unknown: "--unknown",
} as const;

// Optional numeric id <-> key helpers
export const PHASE_BY_ID = PHASE_KEYS;
export const PHASE_ID: Record<PhaseKey, number> =
  Object.fromEntries(PHASE_BY_ID.map((k, i) => [k, i])) as Record<PhaseKey, number>;

export const phaseKeyFromId = (id: number): PhaseKey => PHASE_BY_ID[id] ?? "unknown";
export const phaseIdFromKey = (key: string): number =>
  PHASE_ID[(key as PhaseKey)] ?? PHASE_ID["unknown"];

/** Compact dataset ids used in 'p' arrays (0..4) */
export const PHASE_FROM_COMPACT_ID: Record<number, PhaseKey> = {
  0: "stable",
  1: "metastable",
  2: "transition",
  3: "solitons",
  4: "unknown",
};
export const COMPACT_ID_FROM_PHASE: Record<PhaseKey, number> = {
  stable: 0,
  metastable: 1,
  transition: 2,
  solitons: 3,
  unknown: 4,
};

// =======================
// Run phases (per-run JSON)
// =======================
export const RUN_PHASES = ["order", "chaos", "max"] as const;
export type RunPhase = typeof RUN_PHASES[number];

/** UI text for run-phase buttons, reusing global wording */
export const RUN_PHASE_LABELS: Record<RunPhase, string> = {
  order: PHASE_LABELS.stable,
  chaos: PHASE_LABELS.metastable,
  max: PHASE_LABELS.solitons,
};

/** Map run-phase -> canonical PhaseKey (for CSS color lookup, etc.) */
export const RUN_TO_GLOBAL: Record<RunPhase, PhaseKey> = {
  order: "stable",
  chaos: "metastable",
  max: "solitons",
};

/** Normalize raw tokens (strings or 0/1/2) to RunPhase */
export const RAW_TO_RUN_PHASE: Record<string, RunPhase> = {
  order: "order",
  chaos: "chaos",
  max: "max",
  "0": "order",
  "1": "chaos",
  "2": "max",
};

/** (Legacy) direct raw->global map, if needed by older callers */
export const RUN_PHASE_MAP: Record<string, PhaseKey> = {
  order: "stable",
  chaos: "metastable",
  max: "solitons",
  "0": "stable",
  "1": "metastable",
  "2": "solitons",
};